import { apiGet, apiSend } from './api/client'

type PushConfig = { public_key: string; enabled: boolean }

/**
 * Asking a browser to interrupt somebody.
 *
 * Only ever on a click. A permission prompt that appears on page load is the
 * one every person dismisses without reading, and a dismissed prompt cannot
 * be asked again - so the one chance is spent when they have said they want
 * this.
 */
export async function isPushSupported(): Promise<boolean> {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

export function pushPermission(): NotificationPermission | 'unsupported' {
  return 'Notification' in window ? Notification.permission : 'unsupported'
}

/**
 * The registration, creating it on first use and always checking for a newer
 * script.
 *
 * `updateViaCache: 'none'` matters more than it looks: without it the browser
 * may serve the worker from its HTTP cache for up to 24 hours, so a fix to
 * the worker does not reach anybody who already has the old one - which is
 * exactly how a change to notification behaviour appears not to work.
 */
async function worker(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration('/')

  if (existing) {
    // Cheap, and the only thing that pulls in a new worker promptly.
    void existing.update()

    return existing
  }

  return navigator.serviceWorker.register('/service-worker.js', {
    scope: '/',
    updateViaCache: 'none',
  })
}

/**
 * Brings an already-subscribed browser up to date on every app start.
 *
 * A service worker outlives the page, so somebody who turned notifications on
 * last week is still running last week's worker until something asks for a
 * new one.
 */
export async function refreshWorker(): Promise<void> {
  if (!(await isPushSupported())) return

  const registration = await navigator.serviceWorker.getRegistration('/')

  if (registration) {
    void registration.update()

    return
  }

  // No worker yet, but permission may already have been granted on another
  // tab: registering now means a push can arrive before they open the drawer.
  if (Notification.permission === 'granted') {
    await navigator.serviceWorker.register('/service-worker.js', {
      scope: '/',
      updateViaCache: 'none',
    })
  }
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!(await isPushSupported())) return null

  const registration = await navigator.serviceWorker.getRegistration('/')

  return (await registration?.pushManager.getSubscription()) ?? null
}

/**
 * Subscribes this browser, and tells the server about it.
 *
 * Returns false rather than throwing when the person says no: refusing is an
 * answer, not an error, and an error dialog on top of a refusal is rude.
 */
export async function enablePush(): Promise<boolean> {
  if (!(await isPushSupported())) return false

  const config = await apiGet<PushConfig>('/notification/push')

  if (!config.enabled || config.public_key === '') return false

  const permission = await Notification.requestPermission()

  if (permission !== 'granted') return false

  const registration = await worker()

  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      // Required by every browser: a push that cannot be shown to somebody is
      // not allowed to be sent at all.
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(config.public_key),
    }))

  const json = subscription.toJSON()

  await apiSend('/notification/push', 'POST', {
    endpoint: subscription.endpoint,
    public_key: json.keys?.p256dh ?? '',
    auth_token: json.keys?.auth ?? '',
  })

  return true
}

export async function disablePush(): Promise<void> {
  const subscription = await currentSubscription()

  if (!subscription) return

  await apiSend(`/notification/push?endpoint=${encodeURIComponent(subscription.endpoint)}`, 'DELETE', {})
  await subscription.unsubscribe()
}

/**
 * The VAPID key travels as base64url text and has to reach the browser as
 * bytes. Every implementation of this looks like this because the Push API
 * accepts nothing else.
 */
function urlBase64ToUint8Array(base64: string): ArrayBuffer {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/')

  const raw = window.atob(padded)
  const output = new Uint8Array(raw.length)

  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i)

  // The buffer rather than the view: TypeScript's Uint8Array may be backed by
  // a SharedArrayBuffer, which the Push API does not accept.
  return output.buffer
}
